/*
 * @providesModule SMSVerifyController
 *
 */
import {CountryData} from '@skillz/ui';
import {
  SIGN_IN_PHONE_SUCCESS,
  SIGN_IN_PHONE_UNRECOGNIZED,
  SIGN_IN_USER_SUCCESS,
} from 'AWSConstants';
import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import Flags from 'FlagResource';
import FTUEController from 'FTUEController';
import LoadingIndicator from 'LoadingIndicator';
import NetClient from 'NetClient';
import PhoneNumber from 'PhoneNumber';
import Prefs from 'Preferences';
import SKZConstants from 'SKZConstants';
import SKZPersonalInfo from 'SKZPersonalInfo';
import {SKZUserInterface} from 'SKZUser';
import SkillzImage from 'SkillzImage';
import AuthenticationController, {FTUECredentials} from 'AuthenticationController';
import googleLibphonenumber, {RegionCode} from 'google-libphonenumber';
import React from 'react';
import {ImageStyle, NativeModules, Platform} from 'react-native';
import MergeAccountController from 'MergeAccountController';
import NavigationManager from 'NavigationManager';
import ModalNavigationController from 'ModalNavigationController';
import SkillzRouter from 'SkillzRouter';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';

const AsYouTypeFormatter = googleLibphonenumber.AsYouTypeFormatter;
const PhoneNumberUtil = googleLibphonenumber.PhoneNumberUtil.getInstance();
const SkillzModule = NativeModules.SkillzModule;
const retryMode =
  Platform.OS === 'android' ? true : NativeModules.SKZAPIManager.OnError.failSilently;
const REQUIRE_ACCOUNT_SAVE_KEY = 'needsDepositInfoSKZ';

type ErrorObjectType = {
  name?: string | null;
  param?: string | null;
  message: string;
};

type ErrorResponse = {
  responseObject: ErrorObjectType | ErrorObjectType[];
};

export const OTP_DEBOUNCE_TIME_MS = 100;

class SMSVerifyController {
  //True means we can proceed past the debounce, false means we are turned away
  private static otpDebounce = true;

  /**
   * Method to call confirmVerification with a custom onSuccess callback
   */
  customConfirmVerification = (
    code: string,
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (error: string) => void,
    customOnSuccess: () => void
  ) => {
    const customCallback = () => {
      onSuccess();
      customOnSuccess();
    };
    this.confirmVerification(code, phoneNumber, customCallback, onFailure);
  };

  /**
   * Method to confirm verification code is correct for a new phone number
   * @param code
   * @param phoneNumber
   * @param onSuccess
   * @param onFailure
   */
  confirmVerification = (
    code: string,
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (error: string) => void
  ): void => {
    type userObject = {id: number};
    CurrentUserStore.fetchUser(true)
      .then((user: userObject) => {
        const SkillzAPIManager = NativeModules.SKZAPIManager;
        const url = '/user/v1/phone-verification-request/confirm';
        const patch = {
          token: code,
        };
        NetClient.postURLWithHeaders(
          url,
          patch,
          {'skillz-user-id': user.id},
          SkillzAPIManager.OnError.failSilently,
          (error, payload): null => {
            const errorResponse = error as ErrorResponse;
            if (payload) {
              trackTelemetryEvent({
                event: TelemetryEventName.WITHDRAW_VERIFY_PHONE_CLICK,
                payload: {
                  success: true,
                },
                includeUserWalletDetails: true,
              });
              // Refresh store before calling onSuccess so next screen (e.g. Home T&C) sees isSavedAccount() true and hides footer.
              this.savePhoneNumber(phoneNumber).then(
                () => onSuccess(),
                () => onSuccess()
              );
            } else {
              if (error && !Array.isArray(errorResponse?.responseObject)) {
                trackTelemetryEvent({
                  event: TelemetryEventName.WITHDRAW_VERIFY_PHONE_CLICK,
                  payload: {
                    success: false,
                  },
                  includeUserWalletDetails: true,
                });
                onFailure(errorResponse?.responseObject?.message);
              }
            }
            return null;
          }
        );
      })
      .catch((error: ErrorResponse | null) => {
        // this should never happen
        console.debug(error);
      });
  };

  /**
   * Method to fetch sms prompt count
   *
   */
  fetchPromptCount = async (): Promise<number> => {
    return new Promise((resolve: (value: number) => void) => {
      NetClient.getURL('/user/v1/phone-verification-request/count', {}, retryMode)
        .then((response: {prompt_count: number}) => {
          resolve(response.prompt_count);
        })
        .catch((error: any) => {
          console.debug(`SMSVerifyController: failed to fetch count ${error}`);
        });
    });
  };

  /**
   * Method to fetch country from server
   * @param setCountry
   */
  fetchCountry = (setCountry: React.Dispatch<React.SetStateAction<string>>): void => {
    NetClient.getURL('/location/v1/countries', {}, retryMode)
      .then((theCountry: {country_code: string}) => {
        if (theCountry.country_code) {
          setCountry(theCountry.country_code.toLowerCase());
        }
      })
      .catch((error: any) => {
        console.debug(`SMSVerifyController: failed to fetch country ${error}`);
      });
  };

  /**
   * Method to format phone number given iso2 code for a country
   * @param number unformatted phone number
   * @param iso2
   * @returns
   */
  formatNumber = (number: string, iso2: string): string => {
    let thePhoneNumber = number;
    try {
      const formatter = new AsYouTypeFormatter(iso2);
      const regex = /[^+\d]/g;
      thePhoneNumber = number.replace(regex, '');
      thePhoneNumber = thePhoneNumber.split(' ').join('');
      [...thePhoneNumber].forEach((digit: string) => {
        formatter.inputDigit(digit);
      });
      // @ts-expect-error disable type error due to us trying to access a private property
      thePhoneNumber = formatter.currentOutput_;
      formatter.clear();
    } catch (error) {
      console.log('SAVE_ACCOUNT: Error formatting phone number:', error);
      thePhoneNumber = number;
    }

    return thePhoneNumber;
  };

  /**
   * Method to get country code for a given country
   * @param country
   * @returns country code ex: +1 for US
   */
  getCountryCode = (country: string): string => {
    // Have to make a special exception for Jamaica due to the method we use to verify
    // the phone number is valid expecting the '876' for Jamaica to be area code not
    // country code. This was causing the SDK to send 1876 + 876...(user phone number).
    if (country === 'jm') {
      return '+1';
    }
    const countryData = PhoneNumber.getCountryDataByCode(country);

    return countryData ? `+${countryData.dialCode}` : '+1';
  };

  /**
   * Returns image of country flag for given country
   * @param country
   * @param style
   * @returns Flag image
   */
  getFlag = (country: string, style: ImageStyle): React.ReactElement => {
    if (country) {
      return <SkillzImage source={Flags.get(country)} resizeMode='stretch' style={style} />;
    } else {
      return <SkillzImage source={Flags.get('us')} resizeMode='stretch' style={style} />;
    }
  };

  /**
   * Grabs list of countries
   * @returns Array of all Countries as CountryData Objects
   */
  getCountryData = (): CountryData[] => {
    type CountryObject = {
      name: string;
      iso2: string;
      dialCode: string;
      priority: number;
      areaCodes: String[] | null;
    };
    return PhoneNumber.getAllCountries().map(
      (country: CountryObject, index: number): CountryData => {
        if (country.name[0] === 'Å') {
          country.name = 'Aland Islands';
        }

        return {
          key: index,
          image: Flags.get(country.iso2),
          label: country.name,
          dialCode: `+${country.dialCode}`,
          iso2: country.iso2,
          value: country.name,
        };
      }
    );
  };

  /**
   * Method to get the regeion name for a given phone number
   * @param phoneNumber
   */
  getRegionFromPhoneNumber = (phoneNumber: string): string | null => {
    try {
      // Parse the phone number without assuming a default region
      const parsedNumber = PhoneNumberUtil.parse(phoneNumber);
      // Get the region code
      const region = PhoneNumberUtil.getRegionCodeForNumber(parsedNumber) as
        | RegionCode
        | undefined
        | '001';

      // For 001 (representing non-geographical numbers)
      if (region === '001') {
        return null;
      }

      // Return the lowercase region or null if undefined
      return region ? region.toLowerCase() : null;
    } catch (error) {
      // Log the error and return null when parsing fails
      console.warn('Error determining region from phone number:', error);
      return null;
    }
  };

  /**
   * increment sms prompt count
   *
   */

  incrementPromptCount = (): void => {
    NetClient.postURL('/user/v1/phone-verification-request/count', {}, retryMode, () => {});
  };

  /**
   * Method to determine if phone number is valid for given country
   * @param phoneNumber
   * @param country
   * @returns Whether phone number is valid for given country
   */
  isValidNumber = (phoneNumber: string, country: string): boolean => {
    return PhoneNumber.isValidNumber(phoneNumber, country);
  };

  /**
   * Method to authenticate user with phone number and otp token
   * @param code
   * @param phone
   * @param onSuccess
   * @param onFailure
   */
  loginWithSMS = (
    code: string,
    phone: string,
    onSuccess: () => void,
    onFailure: (error: string) => void,
    autoMerge?: boolean,
    shouldRedirect?: boolean,
    showMergePrompt: boolean = false
  ): void => {
    if (SkillzModule.loginWithSMSEnabled) {
      LoadingIndicator.show();
      AuthenticationController.getFtueCredentials().then((data: FTUECredentials | null) => {
        const hasSeenFTUETutorial = FTUEController.hasCompletedFTUE();
        autoMerge = ClientPropertyConstants.isFTUEv3Enabled() ? true : autoMerge;
        const shouldAutoMerge = autoMerge ?? !hasSeenFTUETutorial;
        Prefs.set({[SKZConstants.FTUE_ACCOUNT_HAS_SEEN_FTUE_TUTORIAL]: hasSeenFTUETutorial});
        //This Prefs needs to be set only for SMS login. Email login flow has an callback that will trigger this flow.
        MergeAccountController.setFtueCredentialsToLocal(
          SKZConstants.FTUE_ACCOUNT_TO_MERGE_SMS_ANDROID,
          JSON.stringify(data)
        );
        SkillzModule.loginUserWithPhoneNumber(
          phone,
          code,
          false,
          (error: any, success: boolean) => {
            if (success) {
              AWSManager.trackEvent(SIGN_IN_PHONE_SUCCESS);
              Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});
              // TODO - Refactor this to avaoid promise chain later on.
              CurrentUserStore.fetchUser()
                .then((primaryUser: SKZUserInterface) => {
                  AWSManager.trackEvent(SIGN_IN_USER_SUCCESS, {data: primaryUser.id});
                  return CurrentUserStore.refreshUserPersonalInfo().catch(() => null);
                })
                .then(() => {
                  if (ClientPropertyConstants.getSMSSaveAccount()) {
                    MergeAccountController.createMergeAccountLink(data)
                      .then(() =>
                        MergeAccountController.startMergeAccountsFlow(
                          data,
                          shouldAutoMerge,
                          shouldRedirect,
                          showMergePrompt
                        )
                      )
                      .catch((mergeError: ErrorResponse) => {
                        LoadingIndicator.hide();
                        MergeAccountController.startAccountMergeErrorSteps(shouldAutoMerge);
                        console.error('Merge Account Link Error: ' + JSON.stringify(mergeError));
                      });
                  } else if (Platform.OS === 'web') {
                    ModalNavigationController.hideModal();
                    setTimeout(() => {
                      LoadingIndicator.hide();
                      if (shouldAutoMerge) {
                        SkillzRouter.backToRoot();
                      }
                    }, 1500);
                    if (!autoMerge) {
                      NavigationManager.popToRoot();
                    }
                  }
                  onSuccess();
                });
            } else {
              LoadingIndicator.hide();
              AWSManager.trackEvent(SIGN_IN_PHONE_UNRECOGNIZED);
              MergeAccountController.removeFTUECredentialsFromLocal(
                SKZConstants.FTUE_ACCOUNT_TO_MERGE_SMS_ANDROID
              );
              if (error) {
                onFailure(error.responseObject.localizedDescription);
              }
            }
          }
        );
      });
    }
  };

  /**
   * Method to request verification code from server
   * @param phoneNumber
   * @param onSuccess
   * @param onFailure
   * @param call
   * @param signUp
   */
  requestVerification = (
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (errorString: string) => void,
    call: boolean | undefined,
    signUp: boolean
  ): void => {
    if (SMSVerifyController.otpDebounce) {
      SMSVerifyController.otpDebounce = false;
      setTimeout(() => {
        SMSVerifyController.otpDebounce = true;
      }, OTP_DEBOUNCE_TIME_MS);
    } else {
      return;
    }

    const SkillzAPIManager = NativeModules.SKZAPIManager;
    const url = '/user/v1/phone-verification-request';
    const patch = {
      phone_number: phoneNumber,
      channel: call ? 'call' : 'sms',
      sign_up: signUp,
    };
    NetClient.postURL(url, patch, SkillzAPIManager.OnError.failSilently, (error, payload) => {
      const errorResponse = error as ErrorResponse;
      if (payload && !error) {
        onSuccess();
        if (signUp) {
          Prefs.set({[REQUIRE_ACCOUNT_SAVE_KEY]: false});
          this.savePhoneNumber(phoneNumber);
        }
      } else {
        if (error && !Array.isArray(errorResponse.responseObject)) {
          SMSVerifyController.otpDebounce = true;
          if (errorResponse?.responseObject?.name === 'PHONE_IN_USE') {
            onFailure(errorResponse?.responseObject?.name);
          } else {
            onFailure(errorResponse?.responseObject?.message);
          }
        }
      }
    });
  };

  /**
   * Method used for requesting sign in code for a user signing in
   * @param phoneNumber
   * @param onSuccess
   * @param onFailure
   * @param call whether to message or call user true = call false = message
   */
  requestVerificationSignIn = (
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (errorString: string | null | undefined) => void,
    call?: boolean | undefined
  ) => {
    this.requestVerification(phoneNumber, onSuccess, onFailure, !!call, false);
  };

  /**
   * Method used for requesting a verification code for verifying a new phone number
   * @param phoneNumber
   * @param onSuccess
   * @param onFailure
   * @param call whether to message or call user true = call false = message
   */
  requestVerificationSignup = (
    phoneNumber: string,
    onSuccess: () => void,
    onFailure: (errorString: string) => void,
    call?: boolean | undefined
  ) => {
    this.requestVerification(phoneNumber, onSuccess, onFailure, !!call, true);
  };

  /**
   * Saves phone number to user account
   * @param phoneNumber
   */
  savePhoneNumber = (phoneNumber: string): Promise<void> => {
    const newUserPersonalPhoneNumber = new SKZPersonalInfo({});
    newUserPersonalPhoneNumber.phone_number = phoneNumber;
    return CurrentUserStore.updateUserPersonalInfo(newUserPersonalPhoneNumber)
      .then(() => {
        console.debug('Phone Number Saved SuccessFully');
        return CurrentUserStore.refreshUserPersonalInfo().catch(() => null);
      })
      .catch((alertError: any) => {
        console.error('Error in saving phone number:', alertError);
      });
  };

  //returns whether or not to show close button for SMS Prompt given client prop value
  showCloseButton(clientProp: string): boolean {
    // show close button unless type is required
    return clientProp !== 'required';
  }

  // returns whether or not to show SMS Prompt given client prop value
  async showPrompt(clientProp: string): Promise<boolean> {
    if (clientProp === 'polite') {
      const maxPrompt = ClientPropertyConstants?.getSMSPromptMaxPrompts();
      const currentCount = await this.fetchPromptCount();
      return maxPrompt > currentCount;
    }
    if (clientProp === 'required' || clientProp === 'always') {
      return true;
    }
    return false;
  }
}

export default new SMSVerifyController();
